/**
 * web/src/components/layout/FlagGuard.tsx
 * Client-side feature-flag render filter. NOT a security boundary — the
 * server's `require_flag` dependency (src/pixel/auth/deps.py) is authoritative
 * and 404s a flagged-off route. FlagGuard merely hides controls that would 404
 * anyway, so the UI matches what the user can actually reach. Mirrors RoleGuard.
 *
 * Reads the SERVER-evaluated flag via `useFlag` (off while /api/me loads), so an
 * unreleased feature never flashes during the auth bootstrap, and the UI and API
 * stay consistent (both derive from the same server evaluation). See
 * docs/FEATURE-FLAGS.md and web/src/lib/flags.ts.
 */
import type { ReactNode } from 'react';
import { useFlag } from '../../lib/flags';

/** Public props for FlagGuard. */
export interface FlagGuardProps {
  /** Flag key — render children iff this flag is on for the user (use FLAGS.*). */
  flag: string;
  /** Children rendered when the flag is on. */
  children: ReactNode;
  /** Optional fallback rendered when the flag is OFF (default: null). */
  fallback?: ReactNode;
}

/**
 * Conditionally render `children` based on whether `flag` is enabled.
 * Renders `fallback` (null by default) while loading or when the flag is off.
 *
 * @sideEffects calls useFlag -> useAuth (subscribes to the /api/me cache)
 * @idempotent yes — multiple calls share the underlying useMe cache entry
 */
export function FlagGuard({ flag, children, fallback = null }: FlagGuardProps) {
  const enabled = useFlag(flag);
  if (!enabled) return <>{fallback}</>;
  return <>{children}</>;
}
